import '@material/web/elevation/elevation';
import { LitElement, css, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './theme-switcher';
import { assetLocations, materialComponentStyles } from '../styles/shared-styles';
import { PageConfig } from '../types/page-config';

@customElement('error-card')
export class ErrorCard extends LitElement {
  @property() private theme =
    (localStorage.getItem('theme') as 'light' | 'dark') ||
    (window.PAGE_CONFIG as PageConfig)?.branding?.theme ||
    'light';
  @property() private errorSummary = (window.PAGE_CONFIG as PageConfig)?.error?.summary || 'An error occurred';
  @property() private errorDetail =
    (window.PAGE_CONFIG as PageConfig)?.error?.detail || 'Please try again or contact support if the problem persists.';
  @property() private stackTrace = (window.PAGE_CONFIG as PageConfig)?.error?.stack || '';

  static styles = [
    materialComponentStyles,
    css`
      :host {
        display: block;
        width: 100%;
        padding: 8px;
        box-sizing: border-box;
        margin: 0;
        /* @ts-expect-error - color-scheme is valid CSS but not recognized by lit-plugin */
        color-scheme: light dark;
        font-family: var(--font-body, 'Public Sans', system-ui, sans-serif);
      }

      :host * {
        box-sizing: border-box;
      }

      .card {
        display: flex;
        flex-direction: column;
        background: var(--md-sys-color-surface);
        color: var(--md-sys-color-on-surface);
        border-radius: 8px;
        border: 1px solid var(--md-sys-color-outline-variant);
        position: relative;
        width: 100%;
        max-width: 500px;
        margin: 0 auto;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
        transform-style: preserve-3d;
        transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        box-shadow:
          hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px,
          hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px;
      }

      .card[data-theme='dark'] {
        box-shadow:
          hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px,
          hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px;
      }

      .card-header {
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }

      .logo-container {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .logo {
        height: 32px;
        width: auto;
      }

      .app-name {
        font-size: 1.25rem;
        font-weight: 500;
      }

      .card-content {
        padding: 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .error-summary {
        font-size: 1.5rem;
        font-weight: 600;
        color: var(--md-sys-color-error);
      }

      .error-detail {
        font-size: 1rem;
        line-height: 1.5;
      }

      .stack-trace {
        background-color: var(--md-sys-color-surface-container);
        color: var(--md-sys-color-on-surface-variant);
        font-family: monospace;
        font-size: 0.875rem;
        padding: 16px;
        border-radius: 4px;
        overflow-x: auto;
        white-space: pre-wrap;
        word-break: break-word;
      }

      .divider {
        border-top: 1px solid var(--md-sys-color-outline-variant);
        margin: 0;
      }

      .actions {
        padding: 16px;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
      }
    `,
  ];

  render(): TemplateResult {
    const logoUri = (window.PAGE_CONFIG as PageConfig)?.branding?.clientLogo || assetLocations.lightLogo;

    return html`
      <div class="card" data-theme="${this.theme}">
        <div class="card-header">
          <div class="logo-container">
            <img class="logo" src="${logoUri}" alt="BlokSec logo" />
          </div>
        </div>
        <div class="card-content">
          <div class="error-summary">${this.errorSummary}</div>
          <div class="error-detail">${this.errorDetail}</div>
          ${
            this.stackTrace
              ? html`<div class="stack-trace">
                  <pre><code>${this.stackTrace}</code></pre>
                </div>`
              : null
          }
        </div>
        <div class="actions">
          <a href="/" class="button primary">Return to sign in</a>
        </div>
      </div>
    `;
  }
}
